import {
    OrgTree,
    Position,
    OrgBoardBuilderService,
    InheritanceObjects,
    FlatSecurityGroupObject,
    SecondaryPositionSecurityGroup,
} from '../../../services/org-board-builder.service';
import {
    deepCopy,
    DisplayValue,
    ECONode,
    isObjectEmpty,
    Organization,
    OrgSecurityGroup,
    OrgPart,
    SecurityTaskOperations,
} from '@newgenus/common';
import { OrgReorderChildrenDialogComponent } from '../../org-reorder-children-dialog/org-reorder-children-dialog.component';
import { SearchableSelectComponent } from '../../../../searchable-select/searchable-select.component';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Component, inject, Inject, OnDestroy, OnInit } from '@angular/core';
import { OrgCreateNewPositionDialogComponent } from '../org-create-position.dialog';
import { OrgEditPositionDialogComponent } from '../org-edit-position-dialog/org-edit-position-dialog.component';
import { OrgBoardService } from '../../../services/org-board-service';
import { JsonPipe, NgClass, NgStyle } from '@angular/common';
import { MaterialModule } from '../../../../material.module';
import { ToastrService } from 'ngx-toastr';
import { debounceTime, Subject, takeUntil } from 'rxjs';
import { NormalizePipe } from '../../../format-pipe';
import { FlatTreeControl } from '@angular/cdk/tree';
import { OrgHierarchyHelpDialogComponent } from './org-hierarchy-help-dialog';
import { DialogService } from '../../../../../services/dialog.service';

@Component({
    selector: 'shared-org-hierarchy-dialog',
    imports: [
        MaterialModule,
        NgStyle,
        NgClass,
        JsonPipe,
        NormalizePipe,
        ReactiveFormsModule,
        FormsModule,
        OrgCreateNewPositionDialogComponent,
        SearchableSelectComponent,
        OrgEditPositionDialogComponent,
    ],
    templateUrl: './org-hierarchy-dialog.html',
    styleUrls: ['./org-hierarchy-dialog.scss', '../../org-dialog.scss', '../../../organization-board.component.scss'],
})
export class OrgHierarchyDialogComponent implements OnInit, OnDestroy {
    //#region Variables
    // Variables for this component that are within scope of "this" component.

    public positionControl = new FormControl('');

    public orgTree: OrgTree | undefined;
    public positions: Position[] = [];
    public flattenedOrgTreeData: any[] = [];
    public selectedSecurityGroups: any;
    public org!: Organization;
    public searchablePositions: DisplayValue[] = [];
    public hoveredPosition: InheritanceComparisonNode | undefined;
    public selectedPosition: InheritanceComparisonNode | undefined;

    /**
     * Whether the expand nodes callback function should process the event or not.
     */
    private expandedNodesDisabled = false;

    /**
     * An array containing the nodes tsecurityGroup have been expanded.
     */
    private expandedNodesForTree5: ExpandedNode[] = [];

    public treeControlSecurityGroups = new FlatTreeControl<OrgNodeChild>(
        (node) => node.level,
        (node) => node.expandable
    );

    public treeFlattenerSecurityGroups = new MatTreeFlattener(
        this._transformerSecurityGroups,
        (node) => node.level,
        (node) => node.expandable,
        (node) => node.children
    );

    public dataSource = new MatTreeFlatDataSource(this.treeControlSecurityGroups, this.treeFlattenerSecurityGroups);

    public orgKey!: string;
    public showLoader = false;
    private destroy$ = new Subject<void>();

    /** Stores the previous isPositionsOn state to restore on dialog close */
    private previousIsPositionsOnState = false;

    public dialog = inject(MatDialog);
    public toastr = inject(ToastrService);
    public obService = inject(OrgBoardService);
    public orgBuilderService = inject(OrgBoardBuilderService);
    public dialogService = inject(DialogService);

    //#endregion

    constructor(
        public dialogRef: MatDialogRef<OrgHierarchyDialogComponent>,
        @Inject(MAT_DIALOG_DATA)
        public data: {
            organization: Organization;
            node: ECONode<any>;
            config: {
                selection: 'single' | 'multiple';
                allowCreate: boolean;
            };
            partTypes?: string[];
            isDeveloper?: boolean;
        }
    ) {
        // Full-bleed on phones (DESIGN.md §Sheets, OE-160); phone-scoped in
        // global-styles.scss, desktop untouched.
        this.dialogRef.addPanelClass('ng-fullbleed-dialog');
    }

    //#region Instantiation
    // Setup\fetches\initialization.

    public ngOnInit(): void {
        // Save the previous isPositionsOn state so we can restore it when dialog closes
        // This prevents the hierarchy dialog from permanently changing the org board's position toggle state
        this.previousIsPositionsOnState = this.orgBuilderService.isPositionsOn;

        // Set isPositionsOn to true ONLY for this dialog's duration
        // This ensures sections and units are visible when the dialog opens
        this.orgBuilderService.isPositionsOn = true;

        // CRITICAL FIX: Use the latest organization from the service if available,
        // as the passed data.organization might be stale (e.g., after reorder operations)
        if (this.obService.organization && this.obService.organization.key === this.data.organization?.key) {
            this.data.organization = this.obService.organization;
        }

        this.updateOrgPositionTree();

        this.setSearchablePositions();

        this.obService.organization$
            .pipe(
                debounceTime(300), // Reduced from 2000ms to 300ms for faster updates
                takeUntil(this.destroy$)
            )
            .subscribe((org) => {
                this.data.organization = org;
                // IMPORTANT: Don't set isPositionsOn here - preserve current state
                this.updateOrgPositionTree();
                this.setSearchablePositions();
            });
    }

    private updateOrgPositionTree() {
        // FIXED: Don't automatically set isPositionsOn to true on organization updates
        // This preserves the user's current view preference and prevents unwanted UI changes
        // Only set to true during initial load if we specifically need to show sections/units

        // DEFENSIVE CHECK: Ensure we have valid organization data
        if (!this.data.organization || !this.data.organization.key) {
            return;
        }

        // DEFENSIVE CHECK: Ensure orgParts exists
        if (!this.data.organization.orgParts || !Array.isArray(this.data.organization.orgParts)) {
            return;
        }

        this.orgTree = this.orgBuilderService.buildOrg(this.data.organization, this.data.organization.key);
        // console.log("🚀 ~ OrgHierarchyDialogComponent ~ updateOrgPositionTree ~ this.orgTree:", this.orgTree)
        this.positions = this.orgTree.positions;
        this.org = this.data.organization;

        setTimeout(() => {
            this.onSelectInitialPart();
        });
    }

    private loadPositions() {
        // Reload positions from the organization data
        this.positions = this.orgTree?.positions || [];
        this.setSearchablePositions();
    }

    //#endregion
    //#region User Interactions
    // Callbacks from html, anything the user can "interact" with from the view.

    public async onSubmit() {
        this.dialogRef.close();
    }

    public onClear() {
        this.positionControl.setValue('');
        this.onSelectInitialPart();
    }

    public createNewPosition(node: any, type: string): void {
        let siblings: any[] = [],
            children: any[] = [];
        const selectedChildren: any = [];
        if (type === 'sibling') {
            siblings = this.findPartSiblings(node);
        } else if (type === 'child') {
            children = node.node?.children || [];
            children.forEach((child) => {
                // if child.data.type === securityGroup then remove child
                if (child.data.type !== 'securityGroup') {
                    selectedChildren.push(child);
                }
            });
        }

        // Get default colors from siblings for new departments
        let defaultColours: { color: string; background: string; linkColor: string } | undefined;
        if (type === 'sibling' && siblings.length > 0) {
            // Use the first sibling's colors as default
            const firstSibling = siblings[0];
            if (firstSibling?.data?.colour) {
                defaultColours = {
                    color: firstSibling.data.colour.color || '#000000',
                    background: firstSibling.data.colour.background || '#ffffff',
                    linkColor: firstSibling.data.colour.linkColor || '#000000',
                };
            }
        } else if (type === 'child' && selectedChildren.length > 0) {
            // Use the first child's colors as default for new children
            const firstChild = selectedChildren[0];
            if (firstChild?.data?.colour) {
                defaultColours = {
                    color: firstChild.data.colour.color || '#000000',
                    background: firstChild.data.colour.background || '#ffffff',
                    linkColor: firstChild.data.colour.linkColor || '#000000',
                };
            }
        }

        const dialog = this.dialog.open(OrgCreateNewPositionDialogComponent, {
            width: '65%',
            height: '80%',
            minHeight: '300px',
            data: {
                organization: this.org,
                type: type,
                node: node,
                siblings: JSON.parse(JSON.stringify(siblings)),
                children: JSON.parse(JSON.stringify(selectedChildren)),
                partTypes: this.data.partTypes || ['executive', 'division', 'department', 'section', 'unit', 'post'],
                defaultColours: defaultColours,
            },
        });

        dialog.afterClosed().subscribe(async (result) => {
            if (result && result.bulkTree) {
                // ── Bulk Builder result ──
                this.showLoader = true;
                try {
                    const anchorPartKey = result.anchorPartKey || node?.node?.data?.partKey;
                    const orgKey = this.data.organization?.key;
                    const graftPoints = result.graftPoints || [];
                    await this.obService.bulkAddParts(
                        orgKey,
                        anchorPartKey,
                        result.insertionType || (type as 'child' | 'sibling'),
                        result.bulkTree,
                        result.siblingOrder || [],
                        graftPoints
                    );
                    let totalPositions = (result.bulkTree || []).length;
                    for (const graft of graftPoints) {
                        totalPositions += graft.children.length;
                    }
                    this.obService.showSuccessToast(
                        `Bulk creation submitted (${totalPositions} org parts). Processing...`,
                        'Success'
                    );
                    this.loadPositions();
                } catch (error) {
                    console.error('Bulk add failed:', error);
                    this.obService.showErrorToast('Bulk creation failed. See console for details.', 'Error');
                }
                this.showLoader = false;
            } else if (result && result.newPart) {
                // Show the orange progress bar at the top
                this.showLoader = true;

                // Determine if the Order has changed.
                let isOrderUnchanged = true;
                const originalArray = type === 'sibling' ? siblings : children;
                // Iterate over siblings/children as they will exclude the new part if there is one (New parts can be positioned at the end of the list and therefore should be excluded from this check).
                isOrderUnchanged = originalArray.every((siblingOrChild) => {
                    const re = result.orderOfAppearance.some((part: any) => {
                        // Determine if the position for the provided sibling/child is still the same as the order of appearance from the result set.
                        return (
                            part.data.partKey === siblingOrChild.data.partKey &&
                            part.position === siblingOrChild.position
                        );
                    });

                    return re;
                });

                // Set ID on part, this is based on the highest ID in the list +1.
                result.newPart.id =
                    (Math.max(...(this.data.organization?.orgParts?.map((part) => part.id) ?? [])) || 0) + 1;

                // Create a temporary partKey for optimistic UI (will be replaced by real one from backend)
                const tempPartKey = `temp-${Date.now()}`;
                result.newPart.partKey = tempPartKey;

                // Add the new part optimistically to the tree with a spinner
                const optimisticNode = this.addOptimisticNodeToTree(node, type, result.newPart);

                try {
                    // Save only the new part (without showing toast immediately).
                    const partKey = await this.obService.saveNewPart(result.newPart, undefined, false);

                    // Check if the other parts will need to be updated as well.
                    if (!isOrderUnchanged) {
                        const partsToUpdate = result.orderOfAppearance
                            .filter((part: any) => part.data.partKey !== result.newPart.partKey)
                            .map((part: any) => part.data);
                        await this.obService.updateParts(partsToUpdate, undefined, false);
                    }

                    // Wait for the backend task to complete
                    const taskResult = await this.obService.waitForTaskCompletion(
                        partKey,
                        SecurityTaskOperations.addPart,
                        30000 // 30 second timeout
                    );

                    // Show toast only after backend task completion
                    if (taskResult.success) {
                        this.obService.showSuccessToast('Part created successfully.', 'Success');
                        // Refresh tree to show the real node from backend
                        this.loadPositions();
                    } else {
                        this.obService.showErrorToast(taskResult.error || 'Failed to process part creation.', 'Error');
                        // Remove the optimistic node on failure
                        this.removeOptimisticNodeFromTree(tempPartKey);
                    }
                } catch (error) {
                    console.error('Error creating position:', error);
                    this.toastr.error('Failed to create position.', 'Error');
                    // Remove the optimistic node on error
                    this.removeOptimisticNodeFromTree(tempPartKey);
                } finally {
                    // Hide the orange progress bar
                    this.showLoader = false;
                }
            }
        });
    }

    /**
     * Add an optimistic node to the tree while waiting for backend confirmation
     */
    private addOptimisticNodeToTree(parentNode: any, type: string, newPart: any): any {
        // Get the tree data
        const treeData = this.dataSource.data;
        if (!treeData || treeData.length === 0) return null;

        // Capture currently expanded node keys BEFORE refreshing
        const expandedKeys = new Set<string>();
        const currentFlatNodes = this.treeControlSecurityGroups.dataNodes || [];
        for (const node of currentFlatNodes) {
            if (this.treeControlSecurityGroups.isExpanded(node)) {
                const key = (node as any)?.data?.partKey || (node as any)?.node?.data?.partKey || (node as any)?.key;
                if (key) expandedKeys.add(key);
            }
        }

        // Find the parent node in the tree structure
        const parentPartKey =
            type === 'sibling'
                ? parentNode?.node?.data?.parentPartKey || parentNode?.data?.parentPartKey
                : parentNode?.node?.data?.partKey || parentNode?.data?.partKey;

        // Create the optimistic node structure
        const optimisticNode: any = {
            data: {
                ...newPart,
                name: newPart.name,
                partKey: newPart.partKey,
                parentPartKey:
                    type === 'sibling' ? parentPartKey : parentNode?.node?.data?.partKey || parentNode?.data?.partKey,
                partType: newPart.partType,
                colour: newPart.colour,
                type: 'part',
            },
            name: newPart.name,
            color: newPart.colour?.color || 'black',
            background: newPart.colour?.background || 'white',
            children: [],
            selected: false,
            showLoader: true, // Show spinner on the optimistic node
            isOptimistic: true, // Mark as optimistic for easy identification
        };

        // Add the node to the tree
        this.insertNodeIntoTree(treeData[0], optimisticNode, parentPartKey, type);

        // Refresh the tree display
        this.dataSource.data = [...treeData];

        // Restore expanded state using captured keys
        const newFlatNodes = this.treeControlSecurityGroups.dataNodes || [];
        for (const node of newFlatNodes) {
            const key = (node as any)?.data?.partKey || (node as any)?.node?.data?.partKey || (node as any)?.key;
            if (key && expandedKeys.has(key)) {
                this.treeControlSecurityGroups.expand(node);
            }
        }

        // Also expand the parent node so the new node is visible
        const parentFlatNode = newFlatNodes.find(
            (n: any) => n?.data?.partKey === parentPartKey || n?.node?.data?.partKey === parentPartKey
        );
        if (parentFlatNode) {
            this.treeControlSecurityGroups.expand(parentFlatNode);
        }

        // Find and return the flattened node for reference
        return newFlatNodes.find(
            (n: any) => n?.data?.partKey === newPart.partKey || n?.node?.data?.partKey === newPart.partKey
        );
    }

    /**
     * Insert a node into the tree structure at the correct position
     */
    private insertNodeIntoTree(treeNode: any, newNode: any, parentPartKey: string, type: string): boolean {
        if (!treeNode) return false;

        const nodePartKey = treeNode?.data?.partKey;

        // If this is the parent node, add as child
        if (type === 'child' && nodePartKey === parentPartKey) {
            if (!treeNode.children) treeNode.children = [];
            treeNode.children.push(newNode);
            return true;
        }

        // If this is a sibling, find the parent and add
        if (type === 'sibling' && nodePartKey === parentPartKey) {
            if (!treeNode.children) treeNode.children = [];
            treeNode.children.push(newNode);
            return true;
        }

        // Recursively search children
        if (treeNode.children && treeNode.children.length > 0) {
            for (const child of treeNode.children) {
                if (this.insertNodeIntoTree(child, newNode, parentPartKey, type)) {
                    return true;
                }
            }
        }

        return false;
    }

    /**
     * Remove an optimistic node from the tree (on failure)
     */
    private removeOptimisticNodeFromTree(tempPartKey: string): void {
        const treeData = this.dataSource.data;
        if (!treeData || treeData.length === 0) return;

        // Capture currently expanded node keys BEFORE refreshing
        const expandedKeys = new Set<string>();
        const currentFlatNodes = this.treeControlSecurityGroups.dataNodes || [];
        for (const node of currentFlatNodes) {
            if (this.treeControlSecurityGroups.isExpanded(node)) {
                const key = (node as any)?.data?.partKey || (node as any)?.node?.data?.partKey || (node as any)?.key;
                if (key) expandedKeys.add(key);
            }
        }

        // Recursively remove the node
        this.removeNodeFromTree(treeData[0], tempPartKey);

        // Refresh the tree display
        this.dataSource.data = [...treeData];

        // Restore expanded state using captured keys
        const newFlatNodes = this.treeControlSecurityGroups.dataNodes || [];
        for (const node of newFlatNodes) {
            const key = (node as any)?.data?.partKey || (node as any)?.node?.data?.partKey || (node as any)?.key;
            if (key && expandedKeys.has(key)) {
                this.treeControlSecurityGroups.expand(node);
            }
        }
    }

    /**
     * Recursively remove a node from the tree by partKey
     */
    private removeNodeFromTree(treeNode: any, partKeyToRemove: string): boolean {
        if (!treeNode || !treeNode.children) return false;

        // Find and remove the node from children
        const index = treeNode.children.findIndex((child: any) => child?.data?.partKey === partKeyToRemove);

        if (index !== -1) {
            treeNode.children.splice(index, 1);
            return true;
        }

        // Recursively search children
        for (const child of treeNode.children) {
            if (this.removeNodeFromTree(child, partKeyToRemove)) {
                return true;
            }
        }

        return false;
    }

    /**
     * Open the reorder children dialog for a node.
     * This allows atomic reordering via the backend reorderChildren operation.
     * Only available if the node has 2 or more children.
     *
     * @param node The tree node whose children will be reordered
     */
    public reorderChildren(node: any): void {
        const partKey = node?.node?.data?.partKey || node?.data?.partKey;

        if (!partKey) {
            this.toastr.error('Cannot identify this position', 'Error');
            return;
        }

        const dialogRef = this.dialog.open(OrgReorderChildrenDialogComponent, {
            width: '500px',
            maxWidth: '90vw',
            maxHeight: '80vh',
            data: {
                organization: this.org,
                parentPartKey: partKey,
            },
        });

        // Subscribe to dialog result to show loader while task is pending
        dialogRef
            .afterClosed()
            .subscribe((result: { success: boolean; processing?: boolean; newChildOrder?: string[] } | undefined) => {
                if (result?.success && result?.processing) {
                    // Show the orange progress bar at the top
                    this.showLoader = true;

                    // Find the child nodes and all their descendants in the tree and show spinners on them
                    const childNodes = this.findChildNodesInTree(partKey, true);
                    childNodes.forEach((childNode) => (childNode.showLoader = true));

                    // Wait for the reorderChildren task to complete on the parent part
                    this.obService
                        .waitForTaskCompletion(partKey, SecurityTaskOperations.reorderChildren, 30000)
                        .then((taskResult) => {
                            // Hide the orange progress bar
                            this.showLoader = false;
                            // Hide spinners on child nodes
                            childNodes.forEach((childNode) => (childNode.showLoader = false));
                            if (taskResult.success) {
                                this.obService.showSuccessToast('Children reordered successfully');
                            } else {
                                this.obService.showErrorToast(
                                    'Reorder operation failed',
                                    taskResult.error || 'Unknown error'
                                );
                            }
                        })
                        .catch((err) => {
                            // Hide the orange progress bar
                            this.showLoader = false;
                            // Hide spinners on child nodes
                            childNodes.forEach((childNode) => (childNode.showLoader = false));
                            console.error('Error waiting for reorder task:', err);
                            // Don't show error toast here - the task might have completed successfully
                            // but we just didn't catch the event in time
                        });
                }
            });
    }

    /**
     * Find all child nodes in the tree for a given parent partKey
     * @param parentPartKey The parent part key to find children for
     * @param includeDescendants If true, recursively includes all descendants
     */
    private findChildNodesInTree(parentPartKey: string, includeDescendants = false): any[] {
        const childNodes: any[] = [];
        const dataNodes = this.treeControlSecurityGroups.dataNodes || [];

        for (const node of dataNodes) {
            // Check various paths where parentPartKey might be stored
            const nodeParentKey =
                (node as any)?.node?.data?.parentPartKey ||
                (node as any)?.data?.parentPartKey ||
                (node as any)?.parentPartKey;
            if (nodeParentKey === parentPartKey) {
                childNodes.push(node);

                // If including descendants, recursively find children of this node
                if (includeDescendants) {
                    const nodePartKey =
                        (node as any)?.node?.data?.partKey || (node as any)?.data?.partKey || (node as any)?.key;
                    if (nodePartKey) {
                        const descendants = this.findChildNodesInTree(nodePartKey, true);
                        childNodes.push(...descendants);
                    }
                }
            }
        }

        return childNodes;
    }

    /**
     * Check if a node has 2 or more children that can be reordered
     *
     * @param node The tree node to check
     * @returns true if the node has 2+ children
     */
    public hasReorderableChildren(node: any): boolean {
        const partKey = node?.node?.data?.partKey || node?.data?.partKey;
        if (!partKey || !this.org?.orgParts) {
            return false;
        }

        const childCount = this.org.orgParts.filter((p: OrgPart) => p.parentPartKey === partKey).length;

        return childCount >= 2;
    }

    public expandAll() {
        this.treeControlSecurityGroups.dataNodes.forEach((dataEntry) =>
            this.treeControlSecurityGroups.expand(dataEntry)
        );
    }

    public collapseAll() {
        this.treeControlSecurityGroups.dataNodes.forEach((dataEntry) =>
            this.treeControlSecurityGroups.collapse(dataEntry)
        );
    }

    /**
     * Collapse tree to show only nodes up to the specified level
     * @param maxLevel Maximum level to show (0 = executives only, 1 = + divisions, 2 = + departments, etc.)
     */
    public collapseToLevel(maxLevel: number) {
        // First collapse all nodes
        this.collapseAll();

        // Then expand nodes up to the specified level
        this.treeControlSecurityGroups.dataNodes.forEach((dataEntry) => {
            if (dataEntry.level <= maxLevel) {
                this.treeControlSecurityGroups.expand(dataEntry);
            }
        });
    }

    public openHelpDialog() {
        this.dialog.open(OrgHierarchyHelpDialogComponent, {
            width: '65%',
            height: '80%',
            minHeight: '300px',
        });
    }

    //#endregion
    //#region Events and Domain Functions
    // $watches/$on events, functions which don't fall into any of the other regions,
    // including any function that has to do domain functionality.

    /**
     * Add or remove the node from the expanded nodes list.
     */
    public onToggleNodeExpand(nodeChild: OrgNodeChild, isExpanded: boolean): void {
        if (this.expandedNodesDisabled) return;

        const arrRef: Array<ExpandedNode> = this.expandedNodesForTree5;

        // Remove node if already in expandedNodes and isExpanded is false.
        if (!isExpanded && arrRef.some((node) => node.level === nodeChild.level && node.id === nodeChild.expandedKey)) {
            // Remove node if already in expandedNodes by slice;
            const index = arrRef.findIndex(
                (node) => node.level === nodeChild.level && node.id === nodeChild.expandedKey
            );
            arrRef.splice(index, 1);
        }

        // Add Node if not in expandedNodes.
        else if (
            isExpanded &&
            !arrRef.some((node) => node.level === nodeChild.level && node.id === nodeChild.expandedKey)
        ) {
            // Push node to expandedNodes.
            arrRef.push({
                level: nodeChild.level,
                // id: nodeChild.key + treeId,
                id: nodeChild.expandedKey,
                node: nodeChild,
            });
        }
        // Sort expandedNodes by level.
        arrRef.sort((a, b) => a.level - b.level);
    }

    public onSelectPosition() {
        // TODO this can be used to action something after selecting a position, like an instant close of the dialog.
        // console.log('Selected position:', this.selectedPosition);
    }

    public onSelectInitialPart(selectedPartKey?: string): void {
        // const securityGroup = securityGroupKey;
        this.dataSource = new MatTreeFlatDataSource(
            this.treeControlSecurityGroups,
            this.treeFlattenerSecurityGroups as any
        );

        // console.log('🔍 DEBUG: About to call buildPositionsTreeFromPart...');
        let unFlattened = this.orgBuilderService.buildPositionsTreeFromPart(this.org, selectedPartKey);
        // console.log("🚀 ~ OrgHierarchyDialogComponent ~ onSelectInitialPart ~ unFlattened:", unFlattened)

        // Empty object would mean there are no positions in the organization.
        // Create a default object to display in the tree instructing the user to create a position.
        if (isObjectEmpty(unFlattened)) {
            // console.log('No data to display');
            unFlattened = {
                selected: true,
                name: 'Create the first position by clicking on plus:',
                color: 'black',
                background: 'white',
                data: {
                    name: 'Create the first position by clicking on plus:',
                },
                children: [],
            };
        }

        // Debug: Log the raw tree structure before sorting (commented out for production)
        // console.log('🔍 Raw tree structure before sorting:', JSON.stringify(unFlattened, (key, value) => {
        //     if (key === 'children' && Array.isArray(value)) {
        //         return value.map(child => ({
        //             name: child.data?.name || child.name,
        //             id: child.data?.id,
        //             depth: child.data?.depth,
        //             partType: child.data?.partType,
        //             partKey: child.data?.partKey,
        //             parentPartKey: child.data?.parentPartKey,
        //             hasChildren: child.children && child.children.length > 0,
        //             childrenCount: child.children ? child.children.length : 0
        //         }));
        //     }
        //     if (key === 'data') {
        //         return {
        //             name: value?.name,
        //             id: value?.id,
        //             depth: value?.depth,
        //             partType: value?.partType,
        //             partKey: value?.partKey,
        //             parentPartKey: value?.parentPartKey
        //         };
        //     }
        //     return value;
        // }, 2));

        // Sort the tree children to ensure consistent ordering
        this.sortTreeChildren(unFlattened);

        this.dataSource.data = [unFlattened];
        this.flattenedOrgTreeData = this.orgBuilderService.flattenPositions(this.dataSource.data[0] as any);
        // console.log('dataSource:', this.dataSource.data);
        // console.log('flattenedOrgTreeData:', this.flattenedOrgTreeData);
        this.expandPreviouslyExpandedNodes();

        this.expandAll();
    }

    public onHoverPosition(treeNode: any) {
        // Add defensive checks for the data structure
        const nodeData = treeNode?.node?.data || treeNode?.data || treeNode;
        if (!nodeData) return;

        this.hoveredPosition = {
            name: nodeData.name,
            key: nodeData.key,
            partKey: nodeData.partKey,
            inheritanceObjects: nodeData.inheritanceObjects || {
                primaryPositionSecurityGroups: [],
                secondaryPositionSecurityGroups: [],
            },
            inheritedSecurityGroupsFlat: nodeData.inheritedSecurityGroupsFlat,
            inheritedSecurityGroupsForPosition: nodeData.inheritedSecurityGroupsForPosition,
        };
    }

    public onClickPosition(treeNode: any) {
        // Add defensive checks for the data structure
        const nodeData = treeNode?.node?.data || treeNode?.data || treeNode;
        if (!nodeData) return;

        this.selectedPosition = {
            name: nodeData.name,
            key: nodeData.key,
            partKey: nodeData.partKey,
            inheritanceObjects: nodeData.inheritanceObjects || {
                primaryPositionSecurityGroups: [],
                secondaryPositionSecurityGroups: [],
            },
            inheritedSecurityGroupsFlat: nodeData.inheritedSecurityGroupsFlat,
            inheritedSecurityGroupsForPosition: nodeData.inheritedSecurityGroupsForPosition,
        };
    }

    //#endregion
    //#region Data Access & Subscriptions
    // API interactions, NGRX selects, etc.

    //#endregion
    //#region Setters, Updaters and Preloaders
    // Setter functions, and methods called from the Instantiation region.

    private setSearchablePositions() {
        const partsCopy = deepCopy(this.data.organization?.orgParts || []);
        this.searchablePositions = partsCopy
            // Sort by depth, lowest to highest.
            .sort((a, b) => a.depth - b.depth)
            .map((part) => ({
                display: part.name,
                value: part.partKey,
            }));
    }

    /**
     * Expand all nodes tsecurityGroup have been expanded before, using the ```expandedNodes``` array.
     */
    private expandPreviouslyExpandedNodes() {
        const treeControl: FlatTreeControl<OrgNodeChild> = this.treeControlSecurityGroups;
        const expandedNodes: ExpandedNode[] = this.expandedNodesForTree5;

        expandedNodes.forEach((prevExpanded) => {
            const dataEntry = treeControl.dataNodes.find(
                (d: any) => d.expandedKey === prevExpanded.node.expandedKey && d.level === prevExpanded.node.level
            );
            if (dataEntry) {
                treeControl.expand(dataEntry);
            }
        });
    }

    private findPartSiblings(mainNode: OrgNodeChild): any[] {
        const parentKey: string | undefined = mainNode?.node?.data?.parentPartKey;
        // console.log("mainNode?.node?.data :", mainNode?.node?.data);

        if (!parentKey) return [];

        const flattenedChildren = this.orgBuilderService.flattenPositions(this.dataSource.data[0] as any);

        const results: any[] = [];
        flattenedChildren.forEach((node) => {
            // Add defensive check for node.data
            if (node?.data?.parentPartKey === parentKey) results.push(node);
        });

        return results;
    }

    //#endregion
    //#region Getters and Filters
    // Getter functions and filter methods/functions.

    //#endregion
    //#region Change Detection
    // Deep config comparisons, change log generation, hasChangesFn, etc.

    //#endregion
    //#region Parsing and Validation
    // Transformation/mapping methods
    // Methods returning true/false: isCurrent, areFeedsEqual, shouldProcess, etc.

    public hasChild = (_: number, node: OrgNodeChild) => node.expandable;

    private _transformerSecurityGroups(node: OrgNode, level: number) {
        return {
            node,
            expandable: !!node.children && node.children.length > 0,
            name: node?.data?.name,
            securityGroupName: node?.data?.securityGroupName,
            userName: node?.data?.userName || '',
            multiUsers: node?.data?.multiUsers,
            inherited: node?.data?.inherited,
            hasPermissions: node?.data?.hasPermissions,
            key: node?.data?.key,
            expandedKey: node?.data?.expandedKey,
            selected: node?.selected,
            type: node?.data?.type,
            partType: node?.data?.partType, // Add partType to the transformed node
            color: node.color,
            background: node.background,
            level: level,
            corruptedData: node?.data?.corruptedData,
            showLoader: (node as any)?.showLoader || false, // Pass through showLoader for optimistic nodes
            isOptimistic: (node as any)?.isOptimistic || false, // Pass through isOptimistic flag
            data: {
                ...node?.data,
                colour: node?.data?.colour, // Pass through the colour data from the original node
            },
        } as OrgNodeChild;
    }

    /**
     * Builds the breadcrumb for the selected position to the security group in the argument.
     * We determine if and how this is done be looking at the partKey and parentPartKey recursively.
     *
     * @example
     * 1. "Div 7 head pos" > Division 7
     * 2. "Role Hat" > Department 19 > Division 7
     *
     * In the above example, the 1st example is a direct child of the Division 7, while the 2nd example is a child of Department 19, which is a child of Division 7.
     */
    public buildBreadCrumb(position: InheritanceComparisonNode, group: SecondaryPositionSecurityGroup) {
        let breadcrumb = '';
        let parentPart = this.org.orgParts.find((p) => p.partKey === group.orgPosition.partKey);

        while (parentPart) {
            if (parentPart.partKey !== position.partKey) {
                breadcrumb = breadcrumb + ' > ' + parentPart.name;
                parentPart = this.org.orgParts.find((p) => p.partKey === parentPart?.parentPartKey);
            } else break;
        }

        return breadcrumb;
    }

    public hasInheritanceFromHoveredNode(treeNode: any): boolean {
        // Add defensive checks for the data structure
        const nodeData = treeNode?.node?.data || treeNode?.data || treeNode;
        if (!nodeData) return false;

        const treeNodePartKey = nodeData.partKey;
        const treeNodeDepth = nodeData.depth;

        let isListedAsInherited =
            this.hoveredPosition?.inheritedSecurityGroupsForPosition?.find(
                (sg) => sg.orgPosition.partKey === treeNodePartKey
            ) !== undefined;

        if (!isListedAsInherited && this.hoveredPosition && this.hoveredPosition.partKey !== treeNodePartKey) {
            // Some parts don't have users on them, but they are still a part of the inheritance chain.
            const inheritedGroups = this.hoveredPosition.inheritedSecurityGroupsForPosition || [];
            for (let i = 0; i < inheritedGroups.length; i++) {
                const sg = inheritedGroups[i];

                let parent = this.org.orgParts.find(
                    (p) => p.partKey === sg.orgPosition.parentPartKey && p.depth <= treeNodeDepth
                );
                while (parent) {
                    if (parent.partKey === treeNodePartKey) {
                        isListedAsInherited = true;
                        break;
                    }

                    if (parent?.parentPartKey) {
                        parent = this.org.orgParts.find((p) => p.partKey === parent?.parentPartKey);
                    } else break;
                }
            }
        }

        return isListedAsInherited;
    }

    public hasInheritanceFromSelectedNode(treeNode: any): boolean {
        // Add defensive checks for the data structure
        const nodeData = treeNode?.data || treeNode?.node?.data;
        if (!nodeData) {
            return false;
        }

        const treeNodePartKey = nodeData.partKey;
        const treeNodeDepth = nodeData.depth;

        let isListedAsInherited =
            this.selectedPosition?.inheritedSecurityGroupsForPosition?.find(
                (sg) => sg.orgPosition.partKey === treeNodePartKey
            ) !== undefined;

        if (!isListedAsInherited && this.selectedPosition && this.selectedPosition.partKey !== treeNodePartKey) {
            // Some parts don't have users on them, but they are still a part of the inheritance chain.
            const inheritedGroups = this.selectedPosition.inheritedSecurityGroupsForPosition || [];
            for (let i = 0; i < inheritedGroups.length; i++) {
                const sg = inheritedGroups[i];

                let parent = this.org.orgParts.find(
                    (p) => p.partKey === sg.orgPosition.parentPartKey && p.depth <= treeNodeDepth
                );
                while (parent) {
                    if (parent.partKey === treeNodePartKey) {
                        isListedAsInherited = true;
                        break;
                    }

                    if (parent?.parentPartKey) {
                        parent = this.org.orgParts.find((p) => p.partKey === parent?.parentPartKey);
                    } else break;
                }
            }
        }

        return isListedAsInherited;
    }

    public hasReverseInheritance(treeNode: any): boolean {
        let hasReverseInheritance = false;

        // Add defensive checks for the data structure
        const nodeData = treeNode?.data || treeNode?.node?.data;
        if (!nodeData || !nodeData.parentPartKey) {
            return false;
        }

        const parentNode = this.flattenedOrgTreeData.find((node) => node.data?.partKey === nodeData.parentPartKey);
        if (parentNode && parentNode.data) {
            // console.log('parentNode:', parentNode);
            // Reverse inheritance will be true if on the parent node, the tree node's part key is listed in the inherited security groups.
            hasReverseInheritance = parentNode.data.inheritedSecurityGroupsForPosition
                ? parentNode.data.inheritedSecurityGroupsForPosition.some(
                      (group: OrgSecurityGroup) => group.orgPosition?.partKey === nodeData.partKey
                  )
                : false;
        }

        return hasReverseInheritance;
    }

    public showInactiveReverseInheritance(treeNode: any): any {
        if (this.hasReverseInheritance(treeNode)) {
            return false;
        }

        // Add defensive checks for the data structure
        const nodeData = treeNode?.data || treeNode?.node?.data;
        return nodeData?.parentPartKey ? true : false;
    }

    //#endregion
    //#region Response Handlers
    // API/NGRX callback handlers.

    //#endregion
    //#region Navigation
    // Confirmation close dialogs, router guards, etc.

    //#endregion
    //#region Post Instantiation
    // Post-init fetch requests, used in such as landing page, after view init, etc.

    //#endregion

    /**
     * Edit a position using the org-edit-position-dialog
     */
    public editPosition(node: any): void {
        // Handle different node structures - the partKey is in node.node.data.partKey
        const partKey = node.node?.data?.partKey || node.partKey || node.key;

        const position = this.org.orgParts.find((p) => p.partKey === partKey);
        if (!position) {
            this.toastr.error('Position not found', 'Error');
            return;
        }

        const dialogRef = this.dialog.open(OrgEditPositionDialogComponent, {
            width: '900px',
            maxWidth: '95vw',
            panelClass: 'ng-resizable-dialog',
            data: {
                part: position,
                partTypes: this.data.partTypes || [],
                orgKey: this.orgKey,
                organization: this.org,
                isDeveloper: this.data.isDeveloper || false,
            },
        });

        dialogRef.afterClosed().subscribe(async (result) => {
            if (result) {
                // Show the orange progress bar at the top
                this.showLoader = true;
                // Refresh the hierarchy after successful edit
                this.loadPositions();
                this.showLoader = false;
            }
        });
    }

    /**
     * Delete a position and all its descendants
     */
    public async deletePosition(node: any): Promise<void> {
        // Handle different node structures - the partKey is in node.node.data.partKey
        const partKey = node.node?.data?.partKey || node.partKey || node.key;

        const position = this.org.orgParts.find((p) => p.partKey === partKey);
        if (!position) {
            this.toastr.error('Position not found', 'Error');
            return;
        }

        // Find all descendants
        const descendants = this.findAllDescendants(position.partKey);
        const totalToDelete = descendants.length + 1; // +1 for the position itself

        let confirmMessage = `Are you sure you want to delete "${position.name}"?`;
        if (descendants.length > 0) {
            confirmMessage += ` This will also delete ${descendants.length} descendant org part(s). Total org parts to be deleted: ${totalToDelete}`;
        }

        const confirmed = await this.dialogService.asyncConfirm('Delete Org Part', confirmMessage);

        if (confirmed) {
            this.performDeletePosition(node, position.partKey, descendants);
        }
    }

    /**
     * Find all descendants of a position
     */
    private findAllDescendants(partKey: string): OrgPart[] {
        const descendants: OrgPart[] = [];
        const directChildren = this.org.orgParts.filter((p) => p.parentPartKey === partKey);

        for (const child of directChildren) {
            descendants.push(child);
            // Recursively find descendants of each child
            descendants.push(...this.findAllDescendants(child.partKey));
        }

        return descendants;
    }

    /**
     * Perform the actual deletion of position and descendants
     */
    private async performDeletePosition(node: any, partKey: string, _descendants: OrgPart[]): Promise<void> {
        // Find the node and all its descendants in the tree to show spinners
        const allAffectedNodes = [node, ...this.findChildNodesInTree(partKey, true)];

        try {
            // Show the orange progress bar at the top
            this.showLoader = true;
            // Show spinner next to the node being deleted and all its descendants
            allAffectedNodes.forEach((n) => (n.showLoader = true));

            // Delete only the parent - the backend task engine will:
            // 1. Find and delete all descendant parts
            // 2. Reindex remaining parts' paths
            // 3. Update affected hats
            // 4. Maintain data integrity
            const deletedPartKey = await this.obService.deleteParts([partKey], this.org, false);

            // Wait for the backend task to complete
            const taskResult = await this.obService.waitForTaskCompletion(
                deletedPartKey,
                SecurityTaskOperations.removePart,
                30000 // 30 second timeout
            );

            // Show toast only after backend task completion
            if (taskResult.success) {
                this.obService.showSuccessToast('Position deleted successfully.', 'Success');
            } else {
                this.obService.showErrorToast(taskResult.error || 'Failed to process position deletion.', 'Error');
            }

            // Refresh the hierarchy
            this.loadPositions();
        } catch (error) {
            console.error('Error deleting position:', error);
            this.toastr.error('Error deleting position', 'Error');
        } finally {
            // Hide the orange progress bar
            this.showLoader = false;
            allAffectedNodes.forEach((n) => (n.showLoader = false));
        }
    }

    /**
     * Recursively sorts tree children by their data.id to ensure consistent ordering
     * and fixes nodes with undefined properties
     * @param node The tree node to sort children for
     */
    private sortTreeChildren(node: any): void {
        // console.log("🚀 ~ OrgHierarchyDialogComponent ~ sortTreeChildren ~ node:", node)

        if (!node) {
            return;
        }

        // Ensure the node has a data property
        if (!node.data) {
            node.data = {};
        }

        if (node.children && Array.isArray(node.children)) {
            const nodeName = node.data?.name || node.name || 'Unknown';
            // const nodeDepth = node.data?.depth || node.depth || 'N/A';
            // console.log(`📝 Processing node: "${nodeName}" (depth: ${nodeDepth}) - Children: ${node.children.length}`);

            // Special debugging for specific nodes if needed (commented out for production)
            // if (nodeName === 'Department 19' || nodeName === 'Department 21') {
            //     console.log(`📦 DEBUG ${nodeName} - Raw children count: ${node.children.length}`);
            //     if (node.children.length > 0) {
            //         console.log(`🔍 ${nodeName} children:`, node.children.map((child: any) => ({
            //             name: child.data?.name || child.name,
            //             partKey: child.data?.partKey || child.partKey,
            //             partType: child.data?.partType || child.partType
            //         })));
            //     } else {
            //         console.log(`ℹ️  ${nodeName} has no children in the organization data (this is normal for leaf departments)`);
            //     }
            // }

            // Filter out completely invalid nodes and fix partially valid ones
            node.children = node.children
                .map((child: any) => {
                    // Ensure child has a data property
                    if (!child.data) {
                        child.data = {};
                    }

                    // Check if this node has any identifying information
                    const hasName = child.data.name !== undefined || child.name !== undefined;
                    const hasPartKey = child.data.partKey !== undefined || child.partKey !== undefined;
                    const hasId = child.data.id !== undefined || child.id !== undefined;
                    const hasPartType = child.data.partType !== undefined || child.partType !== undefined;
                    const hasChildren = child.children && child.children.length > 0;

                    // A node is valid if it has at least a name OR partKey OR id, OR if it has children
                    const isValidNode = hasName || hasPartKey || hasId || hasChildren;

                    if (!isValidNode) {
                        // console.log(`❌ DEBUG: Filtering out invalid child node:`, {
                        //     name: child.data?.name || child.name,
                        //     partKey: child.data?.partKey || child.partKey,
                        //     id: child.data?.id || child.id,
                        //     hasName,
                        //     hasPartKey,
                        //     hasId,
                        //     hasChildren,
                        //     reason: 'All critical properties undefined'
                        // });
                        return null; // Mark for removal
                    }

                    // Fix incomplete data structure by copying from root properties
                    if (child.data.name === undefined && child.name !== undefined) {
                        child.data.name = child.name;
                    }
                    if (child.data.partKey === undefined && child.partKey !== undefined) {
                        child.data.partKey = child.partKey;
                    }
                    if (child.data.id === undefined && child.id !== undefined) {
                        child.data.id = child.id;
                    }
                    if (child.data.partType === undefined && child.partType !== undefined) {
                        child.data.partType = child.partType;
                    }

                    return child;
                })
                .filter((child: any) => child !== null); // Remove null entries

            // console.log(`📊 Children after filtering: ${node.children.length}`);

            // Sort children by path-based sibling index to respect reorder operations
            // Falls back to ID then name if path is not available
            node.children.sort((a: any, b: any) => {
                // Try to extract sibling index from path (e.g., "children.0.children.2.data" -> 2)
                const aPath = a.data?.path;
                const bPath = b.data?.path;
                const aIndex = this.extractIndexFromPath(aPath);
                const bIndex = this.extractIndexFromPath(bPath);

                // If both have valid path indices, use them
                if (aIndex !== -1 && bIndex !== -1) {
                    return aIndex - bIndex;
                }

                const aId = a.data?.id;
                const bId = b.data?.id;
                const aDepth = a.data?.depth || 0;
                const bDepth = b.data?.depth || 0;
                const aName = a.data?.name || '';
                const bName = b.data?.name || '';

                // First, sort by depth to maintain hierarchy
                if (aDepth !== bDepth) {
                    return aDepth - bDepth;
                }

                // Then by ID if both have valid numeric IDs
                if (typeof aId === 'number' && typeof bId === 'number') {
                    return aId - bId;
                }

                // Finally sort by name as fallback
                return aName.localeCompare(bName);
            });

            // Log final structure for key nodes (commented out for production)
            // if (nodeName === 'Department 19' || nodeName === 'Department 21' || node.children.length > 0) {
            //     console.log(`📋 Final children for ${nodeName}:`, node.children.map((child: any) => ({
            //         name: child.data?.name,
            //         id: child.data?.id,
            //         depth: child.data?.depth,
            //         partType: child.data?.partType
            //     })));
            // }

            // Recursively process children
            node.children.forEach((child: any) => this.sortTreeChildren(child));
        }
    }

    /**
     * Extract the sibling index from an OrgPart path string.
     * Path format: "children.X.children.Y.data" where Y is the sibling index
     * @param path The path string (e.g., "children.0.children.2.data")
     * @returns The sibling index (e.g., 2) or -1 if not found
     */
    private extractIndexFromPath(path: string | undefined): number {
        if (!path || typeof path !== 'string') return -1;

        // Split the path and find the last children.X pattern before .data
        const pathParts = path.split('.');

        // Work backwards from the end to find the last children.X pattern
        for (let i = pathParts.length - 1; i >= 0; i--) {
            if (pathParts[i] === 'data' && i > 0 && /^\d+$/.test(pathParts[i - 1])) {
                return parseInt(pathParts[i - 1], 10);
            }
        }

        return -1;
    }

    public ngOnDestroy(): void {
        // Restore the previous isPositionsOn state when dialog closes
        // This ensures the org board toggle state is not affected by opening this dialog
        this.orgBuilderService.isPositionsOn = this.previousIsPositionsOnState;

        this.destroy$.next();
        this.destroy$.complete();
    }
}

interface InheritanceComparisonNode {
    inheritanceObjects: InheritanceObjects;
    inheritedSecurityGroupsFlat: FlatSecurityGroupObject;
    inheritedSecurityGroupsForPosition: OrgSecurityGroup[];
    key: string;
    name: string;
    partKey: string;
}

interface ExpandedNode {
    id: string;
    level: number;
    node: any;
}

interface OrgNodeChild<T = any> {
    background: string;
    color: string;
    expandable: boolean;
    securityGroupName?: string;
    key: string;
    expandedKey: string;
    level: number;
    name: string;
    selected: boolean;
    type: string;
    partType?: string; // Add partType to the interface
    userName: string;
    multiUsers?: boolean;
    inherited?: boolean;
    hasPermissions?: boolean;
    users?: [];
    node: T;
    data?: {
        name: string;
        securityGroupName?: string;
        userName?: string;
        multiUsers?: boolean;
        inherited?: boolean;
        hasPermissions?: boolean;
        type: string;
        partType?: string;
        colour?: {
            background: string;
            color: string;
            linkColor: string;
        };
        user?: { name: string; key?: string };
        key?: string;
        expandedKey?: string;
        corruptedData?: boolean;
        users: [];
    };
}

interface OrgNode {
    name?: string;
    color?: string;
    selected?: boolean;
    background?: string;
    data?: {
        name: string;
        securityGroupName?: string;
        userName?: string;
        multiUsers?: boolean;
        inherited?: boolean;
        hasPermissions?: boolean;
        type: string;
        partType?: string; // Add partType to the data interface
        colour?: {
            background: string;
            color: string;
            linkColor: string;
        };
        user?: { name: string; key?: string };
        key?: string;
        expandedKey?: string;
        corruptedData?: boolean;
        users: [];
    };
    children?: OrgNode[];
}
